import * as Immutable from 'immutable';
import moment from 'moment';

import { changeBigNumberFormat, changeSmallNumberFormat, isNumberType } from '../../shared/helpers';

import { AlphasStoreElement } from './AlphasReducer.types';

export const getAlphaDetail = (state: AlphasStoreElement, id: any) => {
  const myAlphas = state.get('myAlphas').get('results');
  const alphaDetail = myAlphas.filter((alpha: any) => alpha.get('id') === id);

  if (alphaDetail.size >= 1) {
    return alphaDetail.first();
  } else {
    return null;
  }
};

export const setAlphaProp = (state: AlphasStoreElement, { name, value }: any) => {
  if (name === 'tags') {
    return state.setIn(['alphaDetail', name], Immutable.fromJS(value));
  } else {
    return state.setIn(['alphaDetail', name], Immutable.fromJS(value));
  }
};

export const setCorrelationInAlphas = (state: AlphasStoreElement) => {
  const selfCorrelation = state.get('selfCorrelationData');
  const indexCorrelation = selfCorrelation
    .getIn(['schema', 'properties'])
    .findIndex((property: any) => property.get('name') === 'correlation');
  const indexId = selfCorrelation.getIn(['schema', 'properties']).findIndex((property: any) => property.get('name') === 'id');

  const newAlphas = state.get('myAlphas').get('results');

  return newAlphas.map((alpha: any) => {
    const selfAlphaCorrelation = selfCorrelation.get('records').find((el: any) => el.get(indexId) === alpha.get('id'));
    if (selfAlphaCorrelation) {
      return alpha.set('correlation', selfAlphaCorrelation.get(indexCorrelation));
    } else {
      return alpha;
    }
  });
};

interface GetAlphaSummaryProps {
  score?: any;
  type: string;
}

export const getAlphaSummary = (props?: GetAlphaSummaryProps) => {
  // Show score changes if props have score property
  if (props && props.score) {
    return [
      { title: 'alphas.performance.before', tooltip: `tooltip.${props.type}.before`, id: 'before', value: null },
      { title: 'alphas.performance.after', tooltip: `tooltip.${props.type}.after`, id: 'after', value: null },
      { title: 'alphas.performance.change', tooltip: `tooltip.${props.type}.change`, id: 'change', value: null },
    ];
  }

  return [];
};

export const updateAlphasSummary = (score: any, type: string) =>
  getAlphaSummary({ score, type }).map((performance: any) => {
    if (performance.id === 'change') {
      const before = score && score.before ? score.before : 0;
      const after = score && score.after ? score.after : 0;
      const value = score && score.after !== null && score.after !== undefined ? after - before : null;
      performance.value = value;
      if (value !== null) {
        performance.modifiers = value > 0 ? ['positive'] : value < 0 ? ['negative'] : null;
      }

      return performance;
    } else {
      performance.value = score ? score[performance.id] : null;

      return performance;
    }
  });

interface PerformanceStatsProperty {
  id: string;
  type: string;
  title: string;
}

export type FormattedPerformanceStatsProperty = PerformanceStatsProperty & {
  value: string;
  change: string;
  difference: string;
};

const performanceStatsProperties: PerformanceStatsProperty[] = [
  { id: 'sharpe', type: 'decimal', title: 'alphaSummary.sharpe' },
  { id: 'turnover', type: 'percent', title: 'alphaSummary.turnover' },
  { id: 'fitness', type: 'decimal', title: 'alphaSummary.fitness' },
  { id: 'returns', type: 'percent', title: 'alphaSummary.returns' },
  { id: 'drawdown', type: 'percent', title: 'alphaSummary.drawdown' },
  { id: 'margin', type: 'permyriad', title: 'alphaSummary.margin' },
];

export const formatPerformanceStats = (before: any, after: any): FormattedPerformanceStatsProperty[] => {
  return performanceStatsProperties.map(property => {
    const value = after?.[property.id] ?? 0;
    const beforeValue = before?.[property.id] ?? 0;
    const comparisonValue = value - beforeValue;
    return {
      ...property,
      value: changeSmallNumberFormat({ value, type: property.type, hasPercentageSymbol: false }),
      change: comparisonValue === 0 ? 'neutral' : comparisonValue > 0 ? 'positive' : 'negative',
      difference: changeSmallNumberFormat({ value: comparisonValue, type: property.type, hasPercentageSymbol: false }),
    };
  });
};

const performanceHeader = [
  { name: 'year', title: null, columnWidth: 1, index: null },
  { name: 'sharpe', title: null, columnWidth: 3, index: null },
  { name: 'turnover', title: null, columnWidth: 3, index: null },
  { name: 'fitness', title: null, columnWidth: 3, index: null },
  { name: 'returns', title: null, columnWidth: 3, index: null },
  { name: 'drawdown', title: null, columnWidth: 3, index: null },
  { name: 'margin', title: null, columnWidth: 3, index: null },
];

export const formatPerformanceData = (performanceDataBefore: any, performanceDataAfter: any) => {
  const performanceHeaderCompleted =
    performanceHeader &&
    performanceHeader.map((header: any) => {
      const propertyIndex =
        performanceHeader && performanceDataAfter.schema.properties.findIndex((afterProperty: any) => afterProperty.name === header.name);
      if (propertyIndex > -1) {
        const property = performanceHeader && performanceDataAfter.schema.properties[propertyIndex];
        if (property) {
          header.title = property.title;
          header.index = propertyIndex;
          header.type = property.type;
          header.class = isNumberType(property.type);
        }
      }

      return header;
    });

  const tableData = performanceDataAfter.records.map((row: any[], index: number) =>
    performanceHeaderCompleted.map((property: any) => {
      const value = row[property.index];
      const beforeValue =
        performanceDataBefore &&
        performanceDataBefore.records &&
        performanceDataBefore.records[index] &&
        performanceDataBefore.records[index][property.index]
          ? performanceDataBefore.records[index][property.index]
          : 0;
      const comparisonValue = value - beforeValue;
      const change = comparisonValue === 0 ? 'neutral' : comparisonValue > 0 ? 'positive' : 'negative';
      if (property.type === 'decimal' || property.type === 'percent' || property.type === 'permyriad') {
        return {
          difference: changeSmallNumberFormat({ value: comparisonValue, type: property.type, hasPercentageSymbol: false }),
          change,
          value: changeSmallNumberFormat({ value, type: property.type, hasPercentageSymbol: false }),
        };
      } else if (property.type === 'date') {
        return {
          value: value ? moment(value).format('L') : '',
        };
      } else if (typeof value === 'string') {
        return { value };
      } else {
        return {
          difference: changeBigNumberFormat(comparisonValue, 2, property.name),
          change,
          value: changeBigNumberFormat(value, 2, property.name),
        };
      }
    })
  );

  return { tableHeader: performanceHeaderCompleted, tableData: tableData };
};
